import { cn } from "../../../lib/utils";
import {
  Zap,
  AlertTriangle,
  Server,
  Copy,
  CheckCircle2,
  Download,
  Target,
  Activity,
  Timer,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { ModuleDataTable, type ColumnDef } from "../ModuleDataTable";
import { Badge } from "../../badge";
import { Button } from "../../button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";

// Types matching the backend BruteforceResult
interface DiscoveredSubdomain {
  subdomain: string;
  ips: string[];
  source: string; // "wordlist", "permutation", or "adaptive"
}

interface BruteforceStats {
  total_checked: number;
  wordlist_entries: number;
  permutations_generated: number;
  adaptive_candidates: number;
  resolved: number;
  filtered_wildcard: number;
  duration_ms: number;
  queries_per_second: number;
}

export interface BruteforceModuleData {
  domain?: string;
  discovered?: DiscoveredSubdomain[];
  wildcard_detected?: boolean;
  wildcard_ips?: string[];
  stats?: BruteforceStats;
  error?: string;
}

export interface BruteforceModuleViewProps {
  data: BruteforceModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Source colors for discovery method
const SOURCE_COLORS: Record<string, string> = {
  wordlist: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  permutation: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  adaptive: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
};

// Copy button component
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded hover:bg-muted transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-400" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground" />
      )}
    </button>
  );
}

// Export subdomains as CSV
function exportSubdomainsCsv(subdomains: DiscoveredSubdomain[], domain?: string) {
  const headers = ["Subdomain", "IPs", "Discovery Source"];
  const csvContent = [
    headers.join(","),
    ...subdomains.map((s) => [
      `"${s.subdomain}"`,
      `"${s.ips.join("; ")}"`,
      `"${s.source}"`,
    ].join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `bruteforce_${domain || "scan"}_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Export as simple text list
function exportSubdomainsList(subdomains: DiscoveredSubdomain[], domain?: string) {
  const content = subdomains.map((s) => s.subdomain).join("\n");
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `bruteforce_${domain || "scan"}_${new Date().toISOString().split("T")[0]}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Format duration
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = (seconds % 60).toFixed(0);
  return `${minutes}m ${remainingSeconds}s`;
}

export function BruteforceModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: BruteforceModuleViewProps) {
  const discovered = data.discovered || [];
  const stats = data.stats;

  // Count by source
  const sourceCounts = discovered.reduce((acc, sub) => {
    acc[sub.source] = (acc[sub.source] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Subdomain columns
  const columns: ColumnDef<DiscoveredSubdomain>[] = [
    {
      id: "subdomain",
      header: "Subdomain",
      accessor: (row) => row.subdomain,
      cell: (value) => (
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono">{value as string}</code>
          <CopyButton text={value as string} />
        </div>
      ),
      sortable: true,
      filterable: true,
    },
    {
      id: "ips",
      header: "IP Addresses",
      accessor: (row) => row.ips.join(", "),
      cell: (_, row) => (
        <div className="flex flex-wrap gap-1">
          {row.ips.length > 0 ? (
            row.ips.slice(0, 3).map((ip, i) => (
              <code key={i} className="text-xs font-mono bg-muted/50 px-1.5 py-0.5 rounded">
                {ip}
              </code>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">No resolution</span>
          )}
          {row.ips.length > 3 && (
            <Badge variant="outline" className="text-xs">+{row.ips.length - 3}</Badge>
          )}
        </div>
      ),
      sortable: true,
    },
    {
      id: "source",
      header: "Discovery",
      accessor: (row) => row.source,
      cell: (value) => (
        <Badge
          variant="outline"
          className={cn("text-xs capitalize", SOURCE_COLORS[value as string] || "bg-muted")}
        >
          {value === "adaptive" && <Sparkles className="h-3 w-3 mr-1" />}
          {value as string}
        </Badge>
      ),
      filterable: true,
      sortable: true,
    },
  ];

  return (
    <ModuleContainer
      moduleName="subdomains_bruteforce"
      icon={Zap}
      data={data}
      isLoading={isLoading}
      error={error}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Performance Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Target className="h-3 w-3" /> Discovered
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono text-cyan-400">
                {discovered.length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Activity className="h-3 w-3" /> Queries/sec
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">
                {stats?.queries_per_second.toFixed(0) || "—"}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Timer className="h-3 w-3" /> Duration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">
                {stats ? formatDuration(stats.duration_ms) : "—"}
              </p>
            </CardContent>
          </Card>
          <Card className={cn("bg-card/50", data.wildcard_detected && "border-amber-500/30")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Wildcard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold font-mono", data.wildcard_detected ? "text-amber-400" : "text-emerald-400")}>
                {data.wildcard_detected ? "Yes" : "No"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Wildcard Warning */}
        {data.wildcard_detected && data.wildcard_ips && data.wildcard_ips.length > 0 && (
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              <div>
                <h4 className="font-semibold text-amber-400">Wildcard DNS Detected</h4>
                <p className="text-sm text-muted-foreground">
                  The domain has wildcard DNS configured. Results have been filtered to exclude {stats?.filtered_wildcard || 0} false positives.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {data.wildcard_ips.map((ip, i) => (
                    <code key={i} className="text-xs font-mono bg-amber-500/20 px-2 py-1 rounded">
                      {ip}
                    </code>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scan Statistics */}
        {stats && (
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground mb-3">Scan Statistics</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Total Checked</p>
                <p className="font-mono font-semibold">{stats.total_checked.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Wordlist Entries</p>
                <p className="font-mono font-semibold">{stats.wordlist_entries.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Permutations</p>
                <p className="font-mono font-semibold">{stats.permutations_generated.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-cyan-400" /> Adaptive
                </p>
                <p className="font-mono font-semibold text-cyan-400">{stats.adaptive_candidates.toLocaleString()}</p>
              </div>
            </div>
          </div>
        )}

        {/* Discovery Sources Breakdown */}
        {Object.keys(sourceCounts).length > 0 && (
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground mb-2">Discovery Sources</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(sourceCounts).map(([source, count]) => (
                <Badge
                  key={source}
                  variant="outline"
                  className={cn("text-xs capitalize", SOURCE_COLORS[source] || "bg-muted")}
                >
                  {source === "adaptive" && <Sparkles className="h-3 w-3 mr-1" />}
                  {source}: {count}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Discovered Subdomains Table */}
        {discovered.length > 0 && (
          <ModuleSection
            title="Discovered Subdomains"
            icon={Server}
            badge={<Badge variant="outline" className="text-xs">{discovered.length}</Badge>}
          >
            {/* Export Buttons */}
            <div className="flex items-center justify-end gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportSubdomainsList(discovered, data.domain)}
                className="gap-1.5"
              >
                <Download className="h-4 w-4" />
                TXT
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportSubdomainsCsv(discovered, data.domain)}
                className="gap-1.5"
              >
                <Download className="h-4 w-4" />
                CSV
              </Button>
            </div>

            <ModuleDataTable
              data={discovered}
              columns={columns}
              paginated={discovered.length > 15}
              searchable
              searchPlaceholder="Search subdomains..."
            />
          </ModuleSection>
        )}

        {/* Empty State */}
        {discovered.length === 0 && !isLoading && !error && (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Subdomains Discovered</h3>
            <p className="text-sm text-muted-foreground">
              The bruteforce scan did not find any new subdomains for this domain.
            </p>
          </div>
        )}
      </div>
    </ModuleContainer>
  );
}

BruteforceModuleView.displayName = "BruteforceModuleView";
